import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card } from '@promtver/ui-kit';
import { B2BRequestKind, B2BRequestStatus } from '@promtver/contracts';

import { getB2BRequests, getCoordinator, getPartnerEnterprise, updateB2BRequestStatus } from '../../api/partner';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { Select } from '../../components/Select';
import { ru } from '../../i18n/ru';
import { formatDateTimeLocal } from '../../lib/datetime';

function requestKindLabel(kind: B2BRequestKind | string): string {
  return kind === B2BRequestKind.BusinessVisit ? ru.cabinet.requestBusiness : ru.cabinet.requestOnboarding;
}

function requestStatusLabel(status: B2BRequestStatus | string): string {
  switch (status) {
    case B2BRequestStatus.New:
      return ru.cabinet.statusNew;
    case B2BRequestStatus.InProgress:
      return ru.cabinet.statusInProgress;
    case B2BRequestStatus.Answered:
      return ru.cabinet.statusAnswered;
    case B2BRequestStatus.Rejected:
      return ru.cabinet.statusRejected;
    default:
      return ru.common.notSpecified;
  }
}

/**
 * Кабинет предприятия: входящие B2B-заявки и деловые визиты, контакты
 * координатора Платформы, статус обращений (FR-B2B-04).
 */
export function CabinetPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const enterpriseQuery = useQuery({
    queryKey: ['partner', 'enterprise'],
    queryFn: getPartnerEnterprise,
  });

  const requestsQuery = useQuery({
    queryKey: ['partner', 'b2b-requests'],
    queryFn: () => getB2BRequests(),
  });

  const coordinatorQuery = useQuery({
    queryKey: ['partner', 'coordinator'],
    queryFn: getCoordinator,
    retry: false,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: B2BRequestStatus }) =>
      updateB2BRequestStatus(id, status),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['partner', 'b2b-requests'] });
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : ru.common.unknownError),
  });

  const requests = requestsQuery.data?.items ?? [];
  const enterprise = enterpriseQuery.data;
  const coordinator = coordinatorQuery.data;

  return (
    <AppShell title={ru.cabinet.title} subtitle={ru.cabinet.subtitle} active="cabinet">
      <Card title={ru.cabinet.enterprise}>
        <AsyncState
          isLoading={enterpriseQuery.isLoading}
          isError={enterpriseQuery.isError}
          onRetry={() => void enterpriseQuery.refetch()}
        >
          {enterprise ? (
            <div className="flex flex-col gap-1">
              <KeyValue label={ru.cabinet.enterprise} value={enterprise.name} />
              <KeyValue label={ru.group.district} value={enterprise.districtName ?? enterprise.districtId} />
              <KeyValue label={ru.common.address} value={enterprise.address} />
              <KeyValue label={ru.common.phone} value={enterprise.contactPhone ?? ru.cabinet.contactPhoneMissing} mono />
              <KeyValue label={ru.common.email} value={enterprise.contactEmail ?? ru.common.notSpecified} mono />
            </div>
          ) : null}
        </AsyncState>
      </Card>

      <Card title={ru.cabinet.coordinator}>
        {coordinatorQuery.isError ? (
          <p className="text-sm text-muted">{ru.common.notSpecified}</p>
        ) : coordinator ? (
          <div className="flex flex-col gap-1">
            <KeyValue label={ru.cabinet.coordinator} value={coordinator.name} />
            <KeyValue
              label={ru.cabinet.coordinatorPhone}
              value={coordinator.phone ?? ru.cabinet.contactPhoneMissing}
              mono
            />
            <KeyValue
              label={ru.cabinet.coordinatorEmail}
              value={coordinator.email ?? ru.common.notSpecified}
              mono
            />
          </div>
        ) : (
          <p className="text-sm text-muted">{ru.common.loading}</p>
        )}
      </Card>

      <Card title={ru.cabinet.requests}>
        <AsyncState
          isLoading={requestsQuery.isLoading}
          isError={requestsQuery.isError}
          isEmpty={requests.length === 0}
          emptyTitle={ru.cabinet.noRequests}
          emptyDescription={ru.cabinet.noRequestsDescription}
          onRetry={() => void requestsQuery.refetch()}
        >
          <ul className="flex flex-col gap-3">
            {requests.map((request) => (
              <li key={request.id} className="rounded-card border border-line p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <strong className="text-sm">{requestKindLabel(request.kind)}</strong>
                  <Badge tone={request.status === B2BRequestStatus.New ? 'accent' : 'default'}>
                    {requestStatusLabel(request.status)}
                  </Badge>
                </div>

                <div className="flex flex-col gap-1">
                  <KeyValue label={ru.cabinet.contact} value={request.contactName} />
                  <KeyValue label={ru.common.phone} value={request.contactPhone} mono />
                  <KeyValue label={ru.common.email} value={request.contactEmail ?? ru.common.notSpecified} mono />
                  {request.companyName ? (
                    <KeyValue label={ru.cabinet.company} value={request.companyName} />
                  ) : null}
                  {request.district ? (
                    <KeyValue label={ru.cabinet.district} value={request.district} />
                  ) : null}
                  {request.slotsPerWeek ? (
                    <KeyValue label={ru.cabinet.slotsPerWeek} value={request.slotsPerWeek} />
                  ) : null}
                  {request.message ? (
                    <KeyValue label={ru.cabinet.message} value={request.message} />
                  ) : null}
                  <KeyValue label={ru.cabinet.createdAt} value={formatDateTimeLocal(request.createdAt)} />
                </div>

                <div className="mt-3">
                  <Select
                    label={ru.cabinet.changeStatus}
                    value={request.status}
                    onChange={(value) =>
                      statusMutation.mutate({ id: request.id, status: value as B2BRequestStatus })
                    }
                    options={[
                      { value: B2BRequestStatus.New, label: ru.cabinet.statusNew },
                      { value: B2BRequestStatus.InProgress, label: ru.cabinet.statusInProgress },
                      { value: B2BRequestStatus.Answered, label: ru.cabinet.statusAnswered },
                      { value: B2BRequestStatus.Rejected, label: ru.cabinet.statusRejected },
                    ]}
                  />
                </div>
              </li>
            ))}
          </ul>
        </AsyncState>
        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </Card>
    </AppShell>
  );
}
